import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Stack, TextField, Typography } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StyleConstants, Styles } from '../Styles';

const Captcha = forwardRef(({}, ref) => {
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const canvasRef = useRef(null);

  // Generate a random alphanumeric string
  const generateRandomString = (length = 6) => {
    const chars = 'ABDEFGHIJKLMNQRTUYabdefghijkmnrtuy23456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Draw the captcha text and noise onto the canvas
  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Background color
    ctx.fillStyle = '#f4f6f8';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add random background noise lines
    for (let i = 0; i < 6; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 200}, ${Math.random() * 200}, ${Math.random() * 200}, 0.6)`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    // Draw characters with slight rotation and offset
    ctx.font = 'bold 24px monospace';
    ctx.textBaseline = 'middle';
    
    const charWidth = canvas.width / (text.length + 1);
    for (let i = 0; i < text.length; i++) {
      ctx.save();
      const x = charWidth * (i + 1) + (Math.random() * 6 - 3);
      const y = canvas.height / 2 + (Math.random() * 10 - 5);
      
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.4);
      
      ctx.fillStyle = `rgb(${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)})`;
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
  };

  const refreshCaptcha = () => {
    const newText = generateRandomString(6);
    setCaptchaText(newText);
    setUserInput('');
    drawCaptcha(newText);
  };

  useEffect(() => {
    refreshCaptcha();
  }, []);

  // Expose validation and refresh methods to parent via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      return userInput.trim() === captchaText;
    },
    refresh: () => {
      refreshCaptcha();
    }
  }));

  return (
    <Stack direction="column">
      <Stack direction="row">
        <canvas
          ref={canvasRef}
          width={200}
          height={50}
          style={{ borderRadius: '4px', border: '1px solid #dcdcdc' }}
        />
        <IconButton onClick={refreshCaptcha} title="Get new code" sx={{color: StyleConstants.borderColor}}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Typography sx={{ ...Styles.contentFont3 }}>
          Type the characters above:
      </Typography>

      <TextField
        variant="outlined"
        size="small"
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        sx={{
            width: '400px',
            border: '1px solid ' + StyleConstants.borderColor,
        }}
        inputProps={{
            maxLength: 20,
            style: {
                color: StyleConstants.borderColor,
                fontSize: '24px',
            }
        }}
      />
    </Stack>
  );
});

export default Captcha;